import { useMemo, useState } from 'react';
import { api } from '../api';
import { googleMapsLegs, nearestStops, orderStops, type Pt } from '../map/route';
import { visited } from '../state/visited';
import { geocode, locateDevice, type Located } from './SearchBox';

export interface RouteStop extends Pt {
  id: string;
  title?: string;
}

export interface ShopStop extends Pt {
  id: string;
  name: string;
}

interface Built {
  origin: Located;
  /** every stop the planner knew about when the route was built, so a stop can be added back */
  pts: Map<string, Pt & { label: string }>;
}

interface LeftOut {
  id: string;
  why?: string;
}

/**
 * Plan a run: build a suggested order in one tap, then trim and re-order it before opening it in
 * Google Maps. The list is the source of truth once built -- the map numbers, the Google Maps
 * links and "Save route" all follow it, so what you see is what you drive.
 */
export function PlanRun(props: {
  stops: RouteStop[];
  /** listings in view whose address is still hidden (not routable yet) */
  hiddenCount: number;
  shopStops: ShopStop[];
  defaultOrigin: Located | null;
  signedIn: boolean;
  onOrdered: (ids: string[] | null, origin: Pt | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [skipVisited, setSkipVisited] = useState(true);
  const [sprinkle, setSprinkle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [built, setBuilt] = useState<Built | null>(null);
  const [route, setRoute] = useState<string[]>([]);
  const [left, setLeft] = useState<LeftOut[]>([]);
  const [saveMsg, setSaveMsg] = useState('');
  const [announce, setAnnounce] = useState('');

  const label = (id: string) => built?.pts.get(id)?.label ?? 'Stop';

  const legs = useMemo(
    () =>
      built && route.length
        ? googleMapsLegs(
            built.origin,
            route.map((id) => built.pts.get(id)!).filter(Boolean),
          )
        : [],
    [built, route],
  );

  /** Apply a new route and tell the map/list to renumber. */
  function commit(nextRoute: string[], nextLeft: LeftOut[], origin: Pt | null = built?.origin ?? null) {
    setRoute(nextRoute);
    setLeft(nextLeft);
    setSaveMsg('');
    props.onOrdered(nextRoute.length ? nextRoute : null, origin);
  }

  async function build(originFn?: () => Promise<Located>) {
    setErr('');
    setBusy(true);
    try {
      let origin: Located | null = null;
      if (originFn) origin = await originFn();
      else if (from.trim().length >= 2) origin = await geocode(from.trim());
      else origin = props.defaultOrigin;
      if (!origin) throw new Error('Enter where you’re starting from, or use your location.');
      const eventStops = props.stops.filter((s) => !skipVisited || !visited.has(s.id));
      const beenThere = props.stops.filter((s) => skipVisited && visited.has(s.id));
      const shopPicks = sprinkle ? nearestStops(origin, props.shopStops, 3) : [];
      const stops = [...eventStops, ...shopPicks];
      if (!stops.length) throw new Error('No stops left to visit in this list.');
      const ids = orderStops(origin, stops);
      const pts = new Map<string, Pt & { label: string }>();
      for (const s of props.stops) pts.set(s.id, { lat: s.lat, lng: s.lng, label: s.title ?? 'Sale' });
      for (const s of shopPicks) pts.set(s.id, { lat: s.lat, lng: s.lng, label: s.name });
      setBuilt({ origin, pts });
      commit(
        ids,
        beenThere.map((s) => ({ id: s.id, why: 'been there' })),
        origin,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= route.length) return;
    const next = route.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    commit(next, left);
    setAnnounce(`${label(route[i]!)} moved to stop ${j + 1} of ${next.length}`);
  }

  function remove(id: string) {
    commit(
      route.filter((r) => r !== id),
      [...left, { id }],
    );
    setAnnounce(`${label(id)} left out of the route`);
  }

  function addBack(id: string) {
    commit(
      [...route, id],
      left.filter((l) => l.id !== id),
    );
    setAnnounce(`${label(id)} added as the last stop`);
  }

  /** Put the current stops back into the shortest-loop order from the start. */
  function reoptimize() {
    if (!built) return;
    const pts = route.map((id) => ({ id, ...built.pts.get(id)! }));
    commit(orderStops(built.origin, pts), left);
    setAnnounce('Stops re-ordered into the shortest loop');
  }

  function clearRoute() {
    setBuilt(null);
    setRoute([]);
    setLeft([]);
    setSaveMsg('');
    props.onOrdered(null, null);
  }

  async function saveRoute() {
    if (!route.length) return;
    const name = window.prompt('Name this route');
    if (!name || !name.trim()) return;
    setSaveMsg('');
    try {
      const shopIdSet = new Set(props.shopStops.map((s) => s.id));
      const eventOnlyIds = route.filter((id) => !shopIdSet.has(id));
      const { id } = await api.postJson<{ id: string }>('/routes', { name: name.trim() });
      await api.putJson(`/routes/${id}/stops`, { event_ids: eventOnlyIds });
      setSaveMsg(
        eventOnlyIds.length < route.length
          ? "Route saved — shops and other places aren't included in saved routes yet."
          : 'Route saved to your account.',
      );
    } catch {
      setSaveMsg("Couldn't save that route — try again.");
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="plan-toggle"
        onClick={() => setOpen(true)}
        disabled={!props.stops.length}
      >
        <span>Plan a route through these stops</span>
        <span aria-hidden="true">＋</span>
      </button>
    );
  }

  return (
    <section className="plan" aria-labelledby="plan-h">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 id="plan-h" style={{ flex: 1 }}>
          Plan your route
        </h3>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>
      <p className="hint">
        We’ll suggest an order. Then you can drop stops, reorder them, and open the result in Google Maps.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void build();
        }}
      >
        <label htmlFor="plan-from">Starting from</label>
        <input
          id="plan-from"
          placeholder={props.defaultOrigin ? props.defaultOrigin.label : 'Address, city or ZIP'}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={skipVisited}
            onChange={(e) => setSkipVisited(e.target.checked)}
          />
          Skip places I’ve marked as been there
        </label>
        {props.shopStops.length > 0 && (
          <label className="check">
            <input
              type="checkbox"
              checked={sprinkle}
              onChange={(e) => setSprinkle(e.target.checked)}
            />
            Sprinkle in a few nearby shops?
          </label>
        )}
        {props.hiddenCount > 0 && (
          <p className="hint">
            {props.hiddenCount === 1
              ? '1 listing hides its address'
              : `${props.hiddenCount} listings hide their address`}{' '}
            until later, so it isn't in the route yet.
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button className="primary" type="submit" disabled={busy || !props.stops.length}>
            {built ? 'Rebuild route' : 'Build route'}
          </button>
          <button
            type="button"
            disabled={busy || !props.stops.length}
            onClick={() => void build(locateDevice)}
          >
            Start from my location
          </button>
        </div>
      </form>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}

      {built && (
        <div className="plan-edit">
          <h4 id="plan-stops-h">
            Your stops ({route.length})
          </h4>
          {route.length > 0 ? (
            <>
              <p className="hint" style={{ margin: 0 }}>
                Numbered in route order on the map and in the list. Move a stop up or down, or leave it out.
              </p>
              <ol className="plan-stops" aria-labelledby="plan-stops-h">
                {route.map((id, i) => (
                  <li key={id}>
                    <span className="plan-num" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className="plan-label">{label(id)}</span>
                    <span className="plan-btns">
                      <button
                        type="button"
                        aria-label={`Move ${label(id)} up`}
                        disabled={i === 0}
                        onClick={() => move(i, -1)}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${label(id)} down`}
                        disabled={i === route.length - 1}
                        onClick={() => move(i, 1)}
                      >
                        ▼
                      </button>
                      <button type="button" aria-label={`Leave ${label(id)} out of the route`} onClick={() => remove(id)}>
                        Leave out
                      </button>
                    </span>
                  </li>
                ))}
              </ol>
              {route.length > 1 && (
                <button type="button" className="link" onClick={reoptimize}>
                  Re-order to the shortest loop
                </button>
              )}
            </>
          ) : (
            <p className="hint">Every stop is left out. Add some back below to build a route.</p>
          )}

          {left.length > 0 && (
            <>
              <h4>Left out ({left.length})</h4>
              <ul className="plan-stops plan-left">
                {left.map((l) => (
                  <li key={l.id}>
                    <span className="plan-label">
                      {label(l.id)}
                      {l.why ? <span className="hint"> ({l.why})</span> : null}
                    </span>
                    <span className="plan-btns">
                      <button type="button" aria-label={`Add ${label(l.id)} back to the route`} onClick={() => addBack(l.id)}>
                        Add back
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="sr-only" role="status" aria-live="polite">
            {announce}
          </p>
        </div>
      )}

      {legs.length > 0 && (
        <div className="legs" aria-live="polite">
          {legs.map((u, i) => (
            <a key={u} className="btn primary" href={u} target="_blank" rel="noopener">
              {legs.length === 1
                ? 'Open route in Google Maps'
                : `Open part ${i + 1} of ${legs.length} in Google Maps`}
            </a>
          ))}
          {props.signedIn ? (
            <button type="button" onClick={() => void saveRoute()}>
              Save route…
            </button>
          ) : (
            <a href="/account">Sign in to save this route</a>
          )}
          {saveMsg && (
            <p className="hint" role="status">
              {saveMsg}
            </p>
          )}
        </div>
      )}
      {built && (
        <button type="button" className="link" onClick={clearRoute}>
          Clear route
        </button>
      )}
    </section>
  );
}
